import React, { useState, useEffect } from 'react';
import { ShieldCheck, ShieldOff, Copy, Check, Lock, AlertTriangle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { isTauri } from '../../utils/tauri';
import { Row, Section } from '../settingsPrimitives';
import {
  getEncryptionStatus,
  enableEncryption,
  disableEncryption,
  unlockSyncDb,
  lockSyncDb,
} from '../../services/syncEncryption';
import { PasswordInput, PasswordStrengthMeter } from './PasswordInput';

/* ── Local encryption (Phase 9, desktop only) ────────────────────────────────── */
export const LocalEncryptionSection = () => {
  const [status, setStatus]           = useState(null);   // { enabled, locked }
  const [statusError, setStatusError] = useState(null);

  // Enable flow
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [encPass, setEncPass]                 = useState('');
  const [encPassConfirm, setEncPassConfirm]   = useState('');
  const [enableStep, setEnableStep]           = useState('passphrase'); // 'passphrase' | 'recovery'
  const [enableBusy, setEnableBusy]           = useState(false);
  const [enableError, setEnableError]         = useState(null);
  const [recoveryWords, setRecoveryWords]     = useState([]);
  const [confirmIdx, setConfirmIdx]           = useState([0, 1]); // which 2 words to retype
  const [confirmInputs, setConfirmInputs]     = useState(['', '']);
  const [wordsCopied, setWordsCopied]         = useState(false);

  // Disable flow
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePass, setDisablePass]           = useState('');
  const [disableBusy, setDisableBusy]           = useState(false);
  const [disableError, setDisableError]         = useState(null);

  // Unlock (locked-at-startup) flow
  const [unlockSecret, setUnlockSecret] = useState('');
  const [unlockBusy, setUnlockBusy]     = useState(false);
  const [unlockError, setUnlockError]   = useState(null);

  const refresh = async () => {
    try {
      setStatus(await getEncryptionStatus());
      setStatusError(null);
    } catch (err) {
      setStatusError(String(err));
    }
  };
  useEffect(() => { refresh(); }, []);

  const openEnableModal = () => {
    setEncPass(''); setEncPassConfirm(''); setEnableError(null);
    setEnableStep('passphrase'); setRecoveryWords([]); setConfirmInputs(['', '']);
    setWordsCopied(false); setShowEnableModal(true);
  };

  const closeEnableModal = () => {
    if (enableBusy || enableStep === 'recovery') return; // recovery closes only via confirmed Done
    setShowEnableModal(false);
  };

  const handleEnable = async (e) => {
    e?.preventDefault();
    if (encPass.length < 8) { setEnableError('Passphrase must be at least 8 characters.'); return; }
    if (encPass !== encPassConfirm) { setEnableError('Passphrases do not match.'); return; }
    setEnableBusy(true); setEnableError(null);
    try {
      const words = await enableEncryption(encPass);
      setRecoveryWords(words);
      // Forced confirmation: retype two random words of the phrase.
      const a = Math.floor(Math.random() * words.length);
      let b = Math.floor(Math.random() * (words.length - 1));
      if (b >= a) b += 1;
      setConfirmIdx([Math.min(a, b), Math.max(a, b)]);
      setConfirmInputs(['', '']);
      setEnableStep('recovery');
      setEncPass(''); setEncPassConfirm('');
      refresh();
    } catch (err) {
      setEnableError(String(err));
    } finally {
      setEnableBusy(false);
    }
  };

  const recoveryConfirmed =
    recoveryWords.length === 12 &&
    confirmInputs[0].trim().toLowerCase() === recoveryWords[confirmIdx[0]] &&
    confirmInputs[1].trim().toLowerCase() === recoveryWords[confirmIdx[1]];

  const finishRecovery = () => {
    if (!recoveryConfirmed) return;
    setRecoveryWords([]); setConfirmInputs(['', '']);
    setEnableStep('passphrase'); setShowEnableModal(false);
  };

  const handleCopyWords = () => {
    navigator.clipboard.writeText(recoveryWords.join(' '));
    setWordsCopied(true);
    setTimeout(() => setWordsCopied(false), 2000);
  };

  const handleDisable = async () => {
    if (!disablePass) { setDisableError('Enter your passphrase to decrypt.'); return; }
    setDisableBusy(true); setDisableError(null);
    try {
      await disableEncryption(disablePass);
      setShowDisableModal(false); setDisablePass('');
      refresh();
    } catch (err) {
      setDisableError(String(err));
    } finally {
      setDisableBusy(false);
    }
  };

  const handleUnlock = async (e) => {
    e?.preventDefault();
    if (!unlockSecret) return;
    setUnlockBusy(true); setUnlockError(null);
    try {
      await unlockSyncDb(unlockSecret);
      setUnlockSecret('');
      refresh();
    } catch (err) {
      setUnlockError(String(err));
    } finally {
      setUnlockBusy(false);
    }
  };

  const handleLockNow = async () => {
    try { await lockSyncDb(); refresh(); } catch (err) { setStatusError(String(err)); }
  };

  if (!isTauri()) return null; // device-local feature; web build has no apiboo.db

  return (
    <Section title="Local encryption">
      <Row
        label={
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Encrypt local data at rest
            {status?.enabled && (
              <span className="totp-status-badge totp-status-badge--enabled">
                {status.locked ? 'locked' : 'on'}
              </span>
            )}
          </span>
        }
        desc="Encrypts the local sync database (apiboo.db) with a passphrase, so a copied or stolen file is unreadable. Does not protect against malware running while unlocked, and does not encrypt Git-mirrored YAML — use a private repo for private data."
      >
        {status && !status.enabled && (
          <button type="button" className="settings-action-btn flex-shrink-0" onClick={openEnableModal}>
            <Lock size={13} /> Enable…
          </button>
        )}
        {status?.enabled && !status.locked && (
          <button type="button" className="btn-destructive btn-destructive--ghost" style={{ flexShrink: 0 }} onClick={() => { setDisablePass(''); setDisableError(null); setShowDisableModal(true); }}>
            <ShieldOff size={13} /> Disable…
          </button>
        )}
      </Row>

      {statusError && (
        <div className="privacy-feedback privacy-feedback--error" style={{ marginTop: 6 }}>{statusError}</div>
      )}

      {/* Locked at startup: inline unlock (a dedicated lock screen may come later). */}
      {status?.enabled && status.locked && (
        <form onSubmit={handleUnlock}>
          <Row label="Unlock required" desc="The local database is encrypted. Enter your passphrase or 12-word recovery phrase to unlock it for this session.">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <PasswordInput
                value={unlockSecret}
                onChange={(e) => { setUnlockSecret(e.target.value); setUnlockError(null); }}
                placeholder="Passphrase or recovery phrase"
                autoComplete="current-password"
                disabled={unlockBusy}
                style={{ width: 220 }}
              />
              <button type="submit" className="settings-action-btn" disabled={unlockBusy || !unlockSecret}>
                {unlockBusy ? 'Unlocking…' : 'Unlock'}
              </button>
            </div>
          </Row>
          {unlockError && (
            <div className="privacy-feedback privacy-feedback--error" style={{ marginTop: 6 }}>{unlockError}</div>
          )}
        </form>
      )}

      {/* Sub-options only while enabled + unlocked. There is no "Remember on
          this device": it kept the key in the OS keychain, which prompts on
          macOS (removed 26.10.2026). The passphrase is asked at every start. */}
      {status?.enabled && !status.locked && (
        <Row label="Lock now" desc="Close the database and wipe the key from memory until you unlock again. It is asked for again at every start.">
          <button type="button" className="settings-action-btn flex-shrink-0" onClick={handleLockNow}>
            <Lock size={13} /> Lock
          </button>
        </Row>
      )}

      {/* ── Enable modal ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={showEnableModal}
        onClose={closeEnableModal}
        title={enableStep === 'recovery' ? 'Save your recovery phrase' : 'Encrypt local data'}
        size="xl"
        preventBackdropClose={enableBusy || enableStep === 'recovery'}
        preventEscapeClose={enableBusy || enableStep === 'recovery'}
      >
        {enableStep === 'passphrase' ? (
          <form onSubmit={handleEnable}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p className="settings-row-desc" style={{ margin: 0 }}>
                Your local database will be re-encrypted with a key derived from this
                passphrase. You will need it (or the recovery phrase shown next) every
                time the app starts.
              </p>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>
                  Passphrase (min. 8 characters)
                </label>
                <PasswordInput
                  value={encPass}
                  onChange={(e) => { setEncPass(e.target.value); setEnableError(null); }}
                  placeholder="Passphrase"
                  autoComplete="new-password"
                  disabled={enableBusy}
                  autoFocus
                  style={{ width: '100%' }}
                />
                <PasswordStrengthMeter password={encPass} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>
                  Confirm passphrase
                </label>
                <PasswordInput
                  value={encPassConfirm}
                  onChange={(e) => { setEncPassConfirm(e.target.value); setEnableError(null); }}
                  placeholder="Repeat passphrase"
                  autoComplete="new-password"
                  disabled={enableBusy}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <AlertTriangle size={15} style={{ color: '#f0a23b', flexShrink: 0, marginTop: 1 }} />
                <span className="settings-row-desc" style={{ margin: 0 }}>
                  There is no reset. If you lose both the passphrase and the recovery
                  phrase, this data is permanently unrecoverable.
                </span>
              </div>
              {enableError && <div className="privacy-feedback privacy-feedback--error">{enableError}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="settings-action-btn" onClick={closeEnableModal} disabled={enableBusy}>Cancel</button>
                <button type="submit" className="settings-action-btn" disabled={enableBusy || encPass.length < 8 || encPass !== encPassConfirm}>
                  {enableBusy ? 'Encrypting…' : 'Encrypt database'}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={18} style={{ color: '#22c55e', flexShrink: 0 }} />
              <span className="settings-row-label" style={{ color: '#22c55e', margin: 0 }}>Encryption enabled</span>
            </div>
            <p className="settings-row-desc" style={{ margin: 0 }}>
              This 12-word recovery phrase is the ONLY other way to unlock your data if
              you forget the passphrase. It is shown once and never stored anywhere.
              <strong> Lose both and your data is gone — there is no recovery.</strong>
            </p>
            <div className="totp-backup-codes">
              {recoveryWords.map((w, i) => (
                <span key={`${i}-${w}`} className="totp-backup-code-item">{i + 1}. {w}</span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="settings-action-btn" onClick={handleCopyWords}>
                {wordsCopied ? <Check size={13} /> : <Copy size={13} />}
                {wordsCopied ? 'Copied!' : 'Copy phrase'}
              </button>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>
                Confirm you saved it — type word #{confirmIdx[0] + 1} and word #{confirmIdx[1] + 1}:
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[0, 1].map((slot) => (
                  <input
                    key={slot}
                    type="text"
                    className="settings-text-input"
                    placeholder={`Word #${confirmIdx[slot] + 1}`}
                    value={confirmInputs[slot]}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(e) => setConfirmInputs((prev) => {
                      const next = [...prev];
                      next[slot] = e.target.value;
                      return next;
                    })}
                    style={{ flex: 1 }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="settings-action-btn"
                onClick={finishRecovery}
                disabled={!recoveryConfirmed}
                style={{ opacity: recoveryConfirmed ? 1 : 0.4 }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Disable modal ────────────────────────────────────────────────── */}
      <Modal
        isOpen={showDisableModal}
        onClose={() => { if (!disableBusy) setShowDisableModal(false); }}
        title="Disable local encryption"
        type="danger"
        preventBackdropClose={disableBusy}
        preventEscapeClose={disableBusy}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2, #a1a1aa)', margin: 0 }}>
            The database will be decrypted back to plaintext on disk. Enter your
            passphrase (or recovery phrase) to confirm.
          </p>
          <PasswordInput
            placeholder="Passphrase or recovery phrase"
            value={disablePass}
            onChange={(e) => { setDisablePass(e.target.value); setDisableError(null); }}
            autoComplete="current-password"
            disabled={disableBusy}
            autoFocus
            style={{ width: '100%' }}
          />
          {disableError && <div className="privacy-feedback privacy-feedback--error">{disableError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="settings-action-btn" onClick={() => setShowDisableModal(false)} disabled={disableBusy}>Cancel</button>
            <button type="button" className="btn-destructive" onClick={handleDisable} disabled={disableBusy || !disablePass}>
              {disableBusy ? 'Decrypting…' : 'Disable encryption'}
            </button>
          </div>
        </div>
      </Modal>
    </Section>
  );
};
